import { t } from "@/lib/i18n/interpolate";
import {
  type BudgetLabels,
  budgetTable,
  type Format,
  targetLine,
  wordTargets,
} from "@/lib/tool-kit/business-templates/budget";
import type {
  DocBlock,
  DocSection,
  DocumentModel,
} from "@/lib/tool-kit/document";
import {
  paragraphsOrPrompt,
  splitItems,
} from "@/lib/tool-kit/essay-templates/text";

/**
 * A policy memo in two shapes. A decision memo leads with the recommended action, then
 * gives the problem, the background, the options and the next steps — the order most
 * university policy-memo guides teach. A briefing memo informs rather than recommends:
 * bottom line, background, the current situation, what it means, and what to watch.
 *
 * Length is set in single-spaced pages at 500 words a page, split across sections by
 * shares that are a planning convention, not a standard.
 */

export const MEMO_TYPES = ["decision", "briefing"] as const;
export type MemoType = (typeof MEMO_TYPES)[number];

export const SECTIONS: Record<MemoType, readonly string[]> = {
  decision: [
    "summary",
    "problem",
    "background",
    "options",
    "recommendation",
    "next",
  ],
  briefing: ["summary", "background", "situation", "implications", "watch"],
};

/** Percent of the word count per section. Each type sums to 100. */
export const SHARES: Record<MemoType, Record<string, number>> = {
  decision: {
    summary: 15,
    problem: 10,
    background: 15,
    options: 35,
    recommendation: 15,
    next: 10,
  },
  briefing: {
    summary: 15,
    background: 25,
    situation: 30,
    implications: 20,
    watch: 10,
  },
};

export const PAGE_CHOICES = ["1", "2", "3", "5"] as const;
export type PageChoice = (typeof PAGE_CHOICES)[number];

/** Words on a single-spaced page. */
export const WORDS_PER_PAGE = 500;

export interface PolicyMemoInput {
  type: MemoType;
  to: string;
  from: string;
  date: string;
  subject: string;
  /** A decision memo: the problem and why it needs a decision now. A briefing: the current situation. */
  issue: string;
  background: string;
  /** One option per line. */
  options: string;
  recommendation: string;
  pages: PageChoice;
}

export interface PolicyMemoLabels {
  defaultTitle: string;
  titleWithSubject: string;
  subtitles: Record<MemoType, string>;
  subtitlePages: string;
  headerLabels: { to: string; from: string; date: string; subject: string };
  budgetHeading: string;
  budgetNote: string;
  budget: BudgetLabels;
  sectionNames: Record<string, string>;
  tips: Record<MemoType, Record<string, string>>;
  prompts: Record<string, string>;
  target: string;
  targetShare: string;
  tableHead: {
    option: string;
    benefits: string;
    costs: string;
    affected: string;
  };
  needTwo: string;
  checklistHeading: string;
  checklist: Record<MemoType, readonly string[]>;
}

/** The memo's word limit for a page choice. */
export function memoWords(pages: PageChoice): number {
  return Number(pages) * WORDS_PER_PAGE;
}

function headerBlock(
  input: PolicyMemoInput,
  labels: PolicyMemoLabels
): DocBlock {
  const entries: [string, string][] = [
    [labels.headerLabels.to, input.to],
    [labels.headerLabels.from, input.from],
    [labels.headerLabels.date, input.date],
    [labels.headerLabels.subject, input.subject],
  ];
  // A blank value renders as a line to fill in by hand, which suits a printed memo.
  return {
    kind: "fields",
    fields: entries.map(([label, value]) => ({
      label,
      value: value.trim() === "" ? undefined : value.trim(),
    })),
  };
}

function optionsBlocks(
  input: PolicyMemoInput,
  labels: PolicyMemoLabels
): DocBlock[] {
  const options = splitItems(input.options);
  if (options.length === 0) {
    return [{ kind: "prompt", text: labels.prompts.options }];
  }
  const blocks: DocBlock[] = [
    {
      kind: "table",
      head: [
        labels.tableHead.option,
        labels.tableHead.benefits,
        labels.tableHead.costs,
        labels.tableHead.affected,
      ],
      rows: options.map((name) => [name, "", "", ""]),
    },
  ];
  if (options.length < 2) {
    blocks.push({ kind: "prompt", text: labels.needTwo });
  }
  return blocks;
}

function sectionBody(
  key: string,
  input: PolicyMemoInput,
  labels: PolicyMemoLabels
): DocBlock[] {
  switch (key) {
    case "problem":
      return paragraphsOrPrompt(input.issue, labels.prompts.problem);
    case "situation":
      return paragraphsOrPrompt(input.issue, labels.prompts.situation);
    case "background":
      return paragraphsOrPrompt(input.background, labels.prompts.background);
    case "options":
      return optionsBlocks(input, labels);
    case "recommendation":
      return paragraphsOrPrompt(
        input.recommendation,
        labels.prompts.recommendation
      );
    default: {
      const prompt =
        key === "summary" && input.type === "briefing"
          ? labels.prompts.briefingSummary
          : labels.prompts[key];
      return [{ kind: "prompt", text: prompt }];
    }
  }
}

export function buildPolicyMemo(
  input: PolicyMemoInput,
  labels: PolicyMemoLabels,
  fmt: Format
): DocumentModel {
  const keys = SECTIONS[input.type];
  const total = memoWords(input.pages);
  const targets = wordTargets(keys, SHARES[input.type], total, 10);
  const subject = input.subject.trim();
  const sections: DocSection[] = keys.map((key) => ({
    heading: labels.sectionNames[key],
    blocks: [
      {
        kind: "guidance",
        text: `${targetLine(SHARES[input.type][key], targets?.[key] ?? null, { withWords: labels.target, shareOnly: labels.targetShare }, fmt)} ${labels.tips[input.type][key]}`,
      },
      ...sectionBody(key, input, labels),
    ],
  }));
  return {
    title: subject
      ? t(labels.titleWithSubject, { subject })
      : labels.defaultTitle,
    subtitle: `${labels.subtitles[input.type]} · ${t(labels.subtitlePages, { pages: fmt(Number(input.pages)), words: fmt(total) })}`,
    sections: [
      { blocks: [headerBlock(input, labels)] },
      {
        heading: labels.budgetHeading,
        blocks: [
          { kind: "guidance", text: labels.budgetNote },
          budgetTable(
            keys,
            labels.sectionNames,
            SHARES[input.type],
            targets,
            labels.budget,
            fmt
          ),
        ],
      },
      ...sections,
      {
        heading: labels.checklistHeading,
        blocks: [
          {
            kind: "checklist",
            items: labels.checklist[input.type].map((text) => ({ text })),
          },
        ],
      },
    ],
  };
}
